import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
    DemeterDataFrequency,
    DemeterDataSource,
    DemeterPageElementModel,
    DemeterPageType,
    DemeterRegion,
    DemeterSubRegion,
    ExchangeType,
} from '../../../../Generated/Raven-Demeter';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import LinkButton, { LinkButtonType } from '../../Form/Buttons/LinkButton';
import ComponentHeader from '../../Headers/ComponentHeader';
import PageElementBreadcrumb from './PageElementBreadcrumb';
import PageElementContractNumber from './PageElementContractNumber';
import PageElementDataFrequency from './PageElementDataFrequency';
import PageElementExchangeAndCommodity from './PageElementExchangeAndCommodity';
import styles from './PageElementModal.module.scss';
import { IPageElementModalSelection } from './PageElementModalDefinitions';
import PageElementPageType from './PageElementPageType';
import PageElementRegionAndElement from './PageElementRegionAndElement';
import PageElementSearch from './PageElementSearch';

export interface IPageElementModalProps {
    title: string;
    open: boolean;
    pageElements: DemeterPageElementModel[];
    selectedPageElement?: DemeterPageElementModel;
    handleAdd: (selectedPageElement: DemeterPageElementModel) => void;
    handleClose: () => void;
    resetOnOpen?: boolean;
    track: TrackingEvent;
}

const PageElementModalWithoutChart: React.FC<IPageElementModalProps> = (props) => {
    const [translations, translate] = useLanguage();
    const [selectedPageElement, setSelectedPageElement] = useState<DemeterPageElementModel | null>();
    const [selection, setSelection] = useState<IPageElementModalSelection>({});

    const isFuturesCategory = selection.pageType === DemeterPageType.Futures;
    const isPricesCategory = selection.pageType === DemeterPageType.PhysicalPrices;
    const showContractNumbers = isFuturesCategory;
    const showDataFrequency = isPricesCategory;

    const updateSelection = (partial: Partial<IPageElementModalSelection>) => setSelection((previousState) => ({ ...previousState, ...partial }));

    const equals = (a?: string | number | null, b?: string | number | null) => {
        if (a === b || (a == null && b == null) || (!a && !b)) {
            return true;
        }
        return String(a) === String(b);
    };

    const pageTypePageElements = useMemo(() => {
        if (!selection.pageType) {
            return [];
        }

        return props.pageElements.filter((x) => x.pageType === selection.pageType);
    }, [selection.pageType, props.pageElements]);

    const exchangeAndCommodityPageElements = useMemo(() => {
        const { dataSource, exchange, commodityName, subCommodityName, pageType } = selection;

        if (!commodityName && !subCommodityName) {
            return [];
        }

        if (!dataSource && !exchange) {
            return pageTypePageElements.filter((x) => {
                const commodityDisplayName = x.commodityDisplayName ? translate(x.commodityDisplayName) ?? x.commodityDisplayName : x.commodity ?? '';
                const subCommodityDisplayName = x.subCommodityDisplayName ? translate(x.subCommodityDisplayName) ?? x.subCommodityDisplayName : x.subCommodity;
                return equals(commodityDisplayName, commodityName) && equals(subCommodityDisplayName, subCommodityName);
            });
        }

        if (pageType === DemeterPageType.Futures) {
            return pageTypePageElements.filter((x) => {
                const commodityDisplayName = x.commodityDisplayName ? translate(x.commodityDisplayName) ?? x.commodityDisplayName : x.commodity ?? '';
                const subCommodityDisplayName = x.subCommodityDisplayName ? translate(x.subCommodityDisplayName) || x.subCommodityDisplayName : x.subCommodity;
                return equals(x.exchange, exchange) && equals(commodityDisplayName, commodityName) && equals(subCommodityDisplayName, subCommodityName);
            });
        }

        return pageTypePageElements.filter((x) => {
            const commodityDisplayName = x.commodityDisplayName ? translate(x.commodityDisplayName) ?? x.commodityDisplayName : x.commodity ?? '';
            const subCommodityDisplayName = x.subCommodityDisplayName ? translate(x.subCommodityDisplayName) || x.subCommodityDisplayName : x.subCommodity;
            return (
                equals(x.dataSource, dataSource) &&
                equals(x.exchange, exchange) &&
                equals(commodityDisplayName, commodityName) &&
                equals(subCommodityDisplayName, subCommodityName)
            );
        });
    }, [pageTypePageElements, selection, props.pageElements]);

    const regionPageElements = useMemo(() => {
        const { region, subRegion } = selection;
        if (!region && !subRegion) {
            return [];
        }

        return exchangeAndCommodityPageElements.filter((x) => equals(x.region, region) && equals(x.subRegion, subRegion));
    }, [exchangeAndCommodityPageElements, selection, props.pageElements]);

    useEffect(() => {
        if (!props.resetOnOpen) {
            return;
        }

        setSelection({});
    }, [props.open]);

    useEffect(() => {
        if (!props.selectedPageElement) {
            return;
        }

        const pageElement = props.selectedPageElement;
        const commodityName = pageElement.commodityDisplayName
            ? translate(pageElement.commodityDisplayName) ?? pageElement.commodityDisplayName
            : pageElement.commodity ?? '';
        const subCommodityName = pageElement.subCommodityDisplayName
            ? translate(pageElement.subCommodityDisplayName) || pageElement.subCommodityDisplayName
            : pageElement.subCommodity ?? '';

        setSelectedPageElement(pageElement);
        updateSelection({
            pageType: pageElement.pageType,
            dataSource: pageElement.dataSource,
            exchange: pageElement.exchange,
            commodity: pageElement.commodity,
            subCommodity: pageElement.subCommodity,
            region: pageElement.region,
            subRegion: pageElement.subRegion,
            pageElementType: pageElement.pageElementType,
            commodityName,
            subCommodityName,
            subRegionName: pageElement.subRegionDisplayName,
            dataFrequency: pageElement.dataFrequency,
            contractNumber: pageElement.pageType === DemeterPageType.Futures ? +(pageElement.extraParameters ?? 0) : undefined,
        });
    }, [props.selectedPageElement]);

    const handlePageTypeSelect = (pageType: DemeterPageType) =>
        updateSelection({
            pageType,
            dataSource: null,
            exchange: null,
            commodity: null,
            subCommodity: null,
            region: null,
            subRegion: null,
            pageElementType: null,
            commodityName: null,
            subCommodityName: null,
            subRegionName: null,
            contractNumber: undefined,
            dataFrequency: undefined,
        });

    const handleSelectedDataSource = (dataSource: DemeterDataSource | null | undefined) => updateSelection({ dataSource: dataSource ?? null });

    const handleSelectedExchange = (exchange: ExchangeType | null | undefined) => updateSelection({ exchange: exchange ?? null });

    const handleSelectedCommodity = (name: string | null | undefined) => updateSelection({ commodityName: name ?? null });

    const handleSelectedSubCommodity = (name: string | null | undefined) => updateSelection({ subCommodityName: name ?? null });

    const handleSelectedRegion = (region: DemeterRegion | null | undefined) => updateSelection({ region: region ?? null });

    const handleSelectedSubRegion = (subRegion: DemeterSubRegion | null | undefined, name: string | null | undefined) => {
        updateSelection({ subRegion: subRegion ?? null, subRegionName: name ?? null });
    };

    const handleContractSelect = (contractNumber: number) => updateSelection({ contractNumber });

    const handleDataFrequencySelect = (dataFrequency: DemeterDataFrequency) => updateSelection({ dataFrequency });

    const handleSearchPageElement = (value: IPageElementModalSelection) => {
        const selected = props.pageElements.find((pe) => {
            const commodityDisplayName = pe.commodityDisplayName ? translate(pe.commodityDisplayName) ?? pe.commodityDisplayName : pe.commodity ?? '';
            const subCommodityDisplayName = pe.subCommodityDisplayName
                ? translate(pe.subCommodityDisplayName) || pe.subCommodityDisplayName
                : pe.subCommodity ?? '';
            return (
                equals(pe.market, value.market) &&
                equals(pe.pageType, value.pageType) &&
                equals(pe.dataSource, value.dataSource) &&
                equals(pe.exchange, value.exchange) &&
                equals(commodityDisplayName, value.commodityName) &&
                equals(subCommodityDisplayName, value.subCommodityName) &&
                equals(pe.region, value.region) &&
                equals(pe.subRegion, value.subRegion) &&
                equals(pe.extraParameters, value.extraParameters) &&
                equals(pe.dataFrequency, value.dataFrequency)
            );
        });
        setSelectedPageElement(selected);
        setSelection(value);
    };

    const handleAdd = () => {
        if (!selectedPageElement) {
            return;
        }

        props.handleAdd(selectedPageElement);
        props.handleClose();
    };

    const showExchangeAndCommodity =
        selection.pageType !== DemeterPageType.Dashboard &&
        selection.pageType !== DemeterPageType.MonitorsAndIndicators &&
        selection.pageType !== DemeterPageType.NewsAndSummaries;

    const showRegionAndElement =
        selection.pageType !== DemeterPageType.Dashboard &&
        selection.pageType !== DemeterPageType.MonitorsAndIndicators &&
        selection.pageType !== DemeterPageType.NewsAndSummaries &&
        selection.pageType !== DemeterPageType.CommodityIndicators &&
        selection.pageType !== DemeterPageType.HedgeMonitorPro &&
        selection.pageType !== DemeterPageType.Futures;

    const getPageElements = useMemo(() => {
        if (showRegionAndElement) {
            return regionPageElements;
        }
        if (showExchangeAndCommodity) {
            return exchangeAndCommodityPageElements;
        }
        return pageTypePageElements;
    }, [pageTypePageElements, regionPageElements, exchangeAndCommodityPageElements]);

    useEffect(() => {
        const selectedPageElementIsSet =
            getPageElements.length &&
            equals(getPageElements[0].commodity, selection.commodity) &&
            equals(getPageElements[0].subCommodity, selection.subCommodity) &&
            equals(getPageElements[0].region, selection.region) &&
            equals(getPageElements[0].subRegion, selection.subRegion) &&
            equals(getPageElements[0].exchange, selection.exchange) &&
            equals(getPageElements[0].extraParameters, selection.contractNumber);

        if (!getPageElements.length || selectedPageElementIsSet) {
            return;
        }

        setSelectedPageElement({
            ...getPageElements[0],
            contractNumber: selection.contractNumber,
            dataFrequency: selection.dataFrequency ?? getPageElements[0].dataFrequency,
        } as DemeterPageElementModel);
    }, [getPageElements]);

    return (
        <Dialog open={props.open} onClose={props.handleClose} sx={{ '& .MuiDialog-paper': { maxWidth: '1200px' } }}>
            <DialogTitle>
                <div className={styles.page_element_modal_header}>
                    <ComponentHeader title={props.title} />
                    <div className={styles.page_element_modal_search}>
                        <PageElementSearch
                            pageElements={props.pageElements}
                            handleSelect={handleSearchPageElement}
                            showExchangeAndCommodity
                            showRegionAndElement
                            showContractNumbers
                            showDataFrequency
                            track={{ eventCategory: 'ChartBuilder', eventAction: '' }}
                        />
                    </div>
                    <CloseIcon className={styles.page_element_modal_close} onClick={props.handleClose} />
                </div>
            </DialogTitle>
            <DialogContent>
                <PageElementBreadcrumb selected={selection} ignorePageNames />
                <div className={styles.page_element_modal_content}>
                    <div className={styles.page_element_modal_panes}>
                        <div className={styles.page_element_modal_pane}>
                            <PageElementPageType
                                selection={selection}
                                pageElements={props.pageElements}
                                handlePageTypeSelect={handlePageTypeSelect}
                                track={props.track}
                            />
                        </div>
                        {showExchangeAndCommodity && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementExchangeAndCommodity
                                    selection={selection}
                                    pageElements={pageTypePageElements}
                                    handleDataSourceSelect={handleSelectedDataSource}
                                    handleExchangeSelect={handleSelectedExchange}
                                    handleCommoditySelect={handleSelectedCommodity}
                                    handleSubCommoditySelect={handleSelectedSubCommodity}
                                    track={props.track}
                                />
                            </div>
                        )}
                        {showRegionAndElement && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementRegionAndElement
                                    selection={selection}
                                    selectedPageElement={selectedPageElement}
                                    pageElements={exchangeAndCommodityPageElements}
                                    handleRegionSelect={handleSelectedRegion}
                                    handleSubRegionSelect={handleSelectedSubRegion}
                                    track={props.track}
                                />
                            </div>
                        )}
                        {showContractNumbers && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementContractNumber
                                    selection={selection}
                                    contracts={selectedPageElement === null ? [] : [1, 2, 3]}
                                    handleContractSelect={handleContractSelect}
                                    track={props.track}
                                />
                            </div>
                        )}

                        {showDataFrequency && (
                            <div className={styles.page_element_modal_pane}>
                                <PageElementDataFrequency
                                    availableFrequencies={selectedPageElement?.extraParameters ?? ''}
                                    selection={selection}
                                    handleDataFrequencySelect={handleDataFrequencySelect}
                                    track={props.track}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className={styles.page_element_modal_actions}>
                    <LinkButton
                        title={translations.actions.cancel}
                        type={LinkButtonType.White}
                        onClick={props.handleClose}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}CancelClicked` }}
                    />
                    <LinkButton
                        title={translations.actions.add}
                        type={LinkButtonType.Blue}
                        onClick={handleAdd}
                        disabled={!selection.region && !selection.exchange}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}AddClicked` }}
                    />
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default PageElementModalWithoutChart;
